import type { GridApiPremium, GridRowId, GridValidRowModel } from "@mui/x-data-grid-premium";
import {
  GRID_ROOT_GROUP_ID,
  gridExpandedSortedRowIdsSelector,
  gridFilteredSortedRowIdsSelector,
  gridRowNodeSelector,
  gridRowSelectionIdsSelector,
  gridRowSelector,
} from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { sumBy } from "./arrayHelper";
import { defined } from "./typeHelper";

export const selectNexRow = (apiRef: RefObject<GridApiPremium | null>) => selectRow(apiRef, 1);

export const selectPreviousRow = (apiRef: RefObject<GridApiPremium | null>) => selectRow(apiRef, -1);

export const getVisibleRowIds = (apiRef: RefObject<GridApiPremium | null>) => gridExpandedSortedRowIdsSelector(apiRef);

export const getSelectedRowIds = (apiRef: RefObject<GridApiPremium | null>) => gridRowSelectionIdsSelector(apiRef);

export const getAllRowIds = (apiRef: RefObject<GridApiPremium | null>) => gridFilteredSortedRowIdsSelector(apiRef);

export const getAllNonGroupRowIds = (apiRef: RefObject<GridApiPremium | null>) =>
  apiRef.current ? apiRef.current.getAllRowIds() : [];

export const getRowNode = (apiRef: RefObject<GridApiPremium | null>, rowId: GridRowId) =>
  gridRowNodeSelector(apiRef, rowId);

export const getRowsByIds = <T extends GridValidRowModel>(
  apiRef: RefObject<GridApiPremium | null>,
  rowIds: GridRowId[]
): T[] => {
  if (!apiRef.current) {
    return [];
  }

  return rowIds.map((id) => gridRowSelector(apiRef, id)).filter((row): row is T => !!row);
};

const selectRow = (apiRef: RefObject<GridApiPremium | null>, direction: number) => {
  if (!apiRef.current) {
    return;
  }
  const selectedRowIdsModel = getSelectedRowIds(apiRef);
  const selectedRowIds = [...selectedRowIdsModel.keys()];

  if (selectedRowIds.length !== 1) {
    return;
  }

  const selectedRowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(defined(selectedRowIds[0]));
  if (selectedRowIndex === 0 && direction === -1) {
    return;
  }

  const targetRowIndex = selectedRowIndex + direction;
  const targetRowId = getVisibleRowIds(apiRef).find(
    (rowId) => apiRef.current?.getRowIndexRelativeToVisibleRows(rowId) === targetRowIndex
  );

  if (targetRowId) {
    apiRef.current.setRowSelectionModel({ ids: new Set([targetRowId]), type: "include" });
    apiRef.current?.scrollToIndexes({ colIndex: 0, rowIndex: targetRowIndex + direction });
  }
};

export const setAllNonGroupRowsSelection = (apiRef: RefObject<GridApiPremium | null>, isSelected: boolean) => {
  const childRows = getAllNonGroupRowIds(apiRef);
  if (childRows.length > 0) {
    apiRef.current?.selectRows(childRows, isSelected);
  }
};

export const isAnyGroupExpanded = (apiRef: RefObject<GridApiPremium | null>) => {
  return getVisibleRowIds(apiRef).some((id) => {
    const rowNode = getRowNode(apiRef, id);
    return rowNode?.type === "group" && rowNode.childrenExpanded;
  });
};

export const setAllGroupsExpansion = (apiRef: RefObject<GridApiPremium | null>, isExpanded: boolean) => {
  const rowIds = getAllRowIds(apiRef);
  const rowIdsInExpansionOrder = isExpanded ? rowIds : [...rowIds].reverse();

  rowIdsInExpansionOrder.forEach((rowId) => {
    if (getRowNode(apiRef, rowId)?.type === "group") {
      apiRef.current?.setRowChildrenExpansion(rowId, isExpanded);
    }
  });
};

export const checkSelection = (apiRef: RefObject<GridApiPremium | null>) => {
  const children = getAllNonGroupRowIds(apiRef);
  if (children.length === 0) {
    return { children: [], indeterminate: false, checked: false };
  }

  const selectionLookup = new Set(getSelectedRowIds(apiRef).keys());
  const selectedCount = sumBy(children, (child) => (selectionLookup.has(child) ? 1 : 0));

  const indeterminate = selectedCount > 0 && selectedCount < children.length;
  const checked = selectedCount === children.length;

  return { children, indeterminate, checked };
};

export const scrollGridToTop = (apiRef: RefObject<GridApiPremium | null>) => {
  apiRef.current?.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
};

export const refetchGridDataSource = async (apiRef: RefObject<GridApiPremium | null>) => {
  apiRef.current?.setLoading(true);
  await apiRef.current?.dataSource.fetchRows(GRID_ROOT_GROUP_ID, { skipCache: true });
  apiRef.current?.setLoading(false);
};

export const reloadGridDataSource = async (apiRef: RefObject<GridApiPremium | null>, pageSize: number) => {
  scrollGridToTop(apiRef);
  apiRef.current?.setLoading(true);
  apiRef.current?.dataSource.cache.clear();
  apiRef.current?.setRows([]);
  await apiRef.current?.dataSource.fetchRows(GRID_ROOT_GROUP_ID, { start: 0, end: pageSize - 1 });
  apiRef.current?.setLoading(false);
};
